import { useMemo, useState } from "react";

// A system diagram: boxes inside boxes (AWS account → region → VPC → host → compose project) laid
// out by the graph's own grouping, edges drawn between them with what flows. Labels never sit on a
// node, a group's title or another label: each is tried at several points along its edge.

export type NodeKind = "host" | "edge" | "service" | "database" | "volume" | "external" | "unresolved";
export interface ArchNode { id: string; kind: NodeKind; label: string; detail: string[]; column: number; group?: string | null; compact?: boolean; aws?: string; labEdge?: boolean; warning?: string | null; muted?: boolean }
export interface ArchEdge { from: string; to: string; label: string; warning?: string | null; inferred?: boolean }
export type GroupKind = "internet" | "account" | "global" | "region" | "vpc" | "host" | "compose" | "env" | "cluster" | "outside" | "lane";
export interface ArchGroup { id: string; kind: GroupKind; label: string; detail?: string[]; parent: string | null; layout: "row" | "col" | "grid"; rank?: number }
export interface ArchGraph { groups?: ArchGroup[]; columns: string[]; nodes: ArchNode[]; edges: ArchEdge[]; warnings: string[]; requestPaths: string[]; notes: string[] }

export interface Box { x: number; y: number; w: number; h: number }

const NODE_W = 188;
const COMPACT_W = 150;
const PAD = 12;
const ROW_GAP = 44; // room for edges and their labels between boxes side by side
const COL_GAP = 14;
const nodeW = (n: ArchNode) => (n.compact ? COMPACT_W : NODE_W);
const nodeH = (n: ArchNode) => (n.compact ? 28 + (n.detail.length ? 13 : 0) : 32 + Math.min(n.detail.length, 2) * 14);
const headerH = (g: ArchGroup) => (g.kind === "lane" ? 0 : 22 + (g.detail?.length ? 14 : 0));
const pad = (g: ArchGroup) => (g.kind === "lane" ? 0 : PAD);

type Item = { kind: "group"; g: ArchGroup; order: number } | { kind: "node"; n: ArchNode; order: number };
interface Sized { item: Item; w: number; h: number; children?: Sized[]; cols?: number }

/** Lays the graph out: returns absolute boxes for every node and group, and the drawing's size. */
export function layout(g: ArchGraph) {
  const groups = g.groups ?? [];
  const known = new Set(groups.map((x) => x.id));
  const kids = new Map<string | null, Item[]>();
  const push = (parent: string | null, item: Item) => kids.set(parent, [...(kids.get(parent) ?? []), item]);
  groups.forEach((x, i) => push(x.parent && known.has(x.parent) ? x.parent : null, { kind: "group", g: x, order: i }));
  g.nodes.forEach((n, i) => push(n.group && known.has(n.group) ? n.group : null, { kind: "node", n, order: 1000 + i }));
  const rank = (it: Item) => (it.kind === "group" ? (it.g.rank ?? 0) : 0);
  for (const list of kids.values()) list.sort((a, b) => rank(a) - rank(b) || a.order - b.order);

  const size = (it: Item): Sized => {
    if (it.kind === "node") return { item: it, w: nodeW(it.n), h: nodeH(it.n) };
    const ch = (kids.get(it.g.id) ?? []).map(size);
    const p = pad(it.g);
    const head = headerH(it.g);
    const inner = arrange(it.g.layout, ch, it.g.kind === "lane");
    const minW = it.g.kind === "lane" ? 0 : Math.min(320, 16 + it.g.label.length * 6.6);
    return { item: it, w: Math.max(inner.w + 2 * p, minW), h: inner.h + 2 * p + head, children: ch, cols: inner.cols };
  };
  const arrange = (mode: ArchGroup["layout"], ch: Sized[], lane: boolean) => {
    if (!ch.length) return { w: 0, h: 0, cols: 1 };
    if (mode === "row") return { w: ch.reduce((s, c) => s + c.w, 0) + (ch.length - 1) * ROW_GAP, h: Math.max(...ch.map((c) => c.h)), cols: ch.length };
    if (mode === "col") return { w: Math.max(...ch.map((c) => c.w)), h: ch.reduce((s, c) => s + c.h, 0) + (ch.length - 1) * (lane ? COL_GAP + 4 : COL_GAP), cols: 1 };
    const cols = Math.min(ch.length, ch.length <= 3 ? ch.length : Math.ceil(Math.sqrt(ch.length)));
    const { widths, heights } = grid(ch, cols);
    return { w: widths.reduce((a, b) => a + b, 0) + (cols - 1) * 24, h: heights.reduce((a, b) => a + b, 0) + (heights.length - 1) * COL_GAP, cols };
  };
  const grid = (ch: Sized[], cols: number) => {
    const widths = Array.from({ length: cols }, (_, c) => Math.max(...ch.filter((_, i) => i % cols === c).map((x) => x.w)));
    const rows = Math.ceil(ch.length / cols);
    const heights = Array.from({ length: rows }, (_, r) => Math.max(...ch.slice(r * cols, r * cols + cols).map((x) => x.h)));
    return { widths, heights };
  };

  const pos = new Map<string, Box>();
  const groupBoxes: { g: ArchGroup; box: Box; depth: number }[] = [];
  const place = (s: Sized, x: number, y: number, depth: number) => {
    if (s.item.kind === "node") {
      pos.set(s.item.n.id, { x, y, w: s.w, h: s.h });
      return;
    }
    const gr = s.item.g;
    pos.set(gr.id, { x, y, w: s.w, h: s.h });
    if (gr.kind !== "lane") groupBoxes.push({ g: gr, box: { x, y, w: s.w, h: s.h }, depth });
    placeChildren(s.children ?? [], gr.layout, x + pad(gr), y + pad(gr) + headerH(gr), s.cols ?? 1, depth + (gr.kind === "lane" ? 0 : 1), gr.kind === "lane");
  };
  const placeChildren = (ch: Sized[], mode: ArchGroup["layout"], x0: number, y0: number, cols: number, depth: number, lane: boolean) => {
    if (mode === "row") {
      let x = x0;
      for (const c of ch) {
        place(c, x, y0, depth);
        x += c.w + ROW_GAP;
      }
    } else if (mode === "col") {
      let y = y0;
      for (const c of ch) {
        place(c, x0, y, depth);
        y += c.h + (lane ? COL_GAP + 4 : COL_GAP);
      }
    } else {
      const { widths, heights } = grid(ch, cols);
      ch.forEach((c, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        place(c, x0 + widths.slice(0, col).reduce((a, b) => a + b, 0) + col * 24, y0 + heights.slice(0, row).reduce((a, b) => a + b, 0) + row * COL_GAP, depth);
      });
    }
  };
  const top = (kids.get(null) ?? []).map(size);
  placeChildren(top, "row", 8, 8, top.length, 0, false);
  const width = Math.max(...[...pos.values()].map((b) => b.x + b.w), 100) + 12;
  const height = Math.max(...[...pos.values()].map((b) => b.y + b.h), 60) + 12;
  // ancestors, to skip edges from a box into what it holds
  const parentOf = new Map<string, string | null>([...groups.map((x) => [x.id, x.parent] as const), ...g.nodes.map((n) => [n.id, n.group ?? null] as const)]);
  const within = (id: string, anc: string) => {
    for (let p = parentOf.get(id); p; p = parentOf.get(p) ?? null) if (p === anc) return true;
    return false;
  };
  return { width, height, pos, groupBoxes, within };
}

/* ---------------- edges ---------------- */

interface Curve { d: string; at: (t: number) => [number, number] }

function curve(a: Box, b: Box): Curve | null {
  let p0: [number, number], p3: [number, number], c1: [number, number], c2: [number, number];
  if (b.x >= a.x + a.w - 4) {
    p0 = [a.x + a.w, a.y + a.h / 2]; p3 = [b.x - 5, b.y + Math.min(b.h / 2, 24)];
    const dx = Math.max(24, (p3[0] - p0[0]) / 2);
    c1 = [p0[0] + dx, p0[1]]; c2 = [p3[0] - dx, p3[1]];
  } else if (b.x + b.w <= a.x + 4) {
    p0 = [a.x, a.y + a.h / 2]; p3 = [b.x + b.w + 5, b.y + Math.min(b.h / 2, 24)];
    const dx = Math.max(24, (p0[0] - p3[0]) / 2);
    c1 = [p0[0] - dx, p0[1]]; c2 = [p3[0] + dx, p3[1]];
  } else if (b.y >= a.y + a.h) {
    p0 = [a.x + a.w / 2, a.y + a.h]; p3 = [b.x + b.w / 2, b.y - 5];
    const dy = Math.max(16, (p3[1] - p0[1]) / 2);
    c1 = [p0[0], p0[1] + dy]; c2 = [p3[0], p3[1] - dy];
  } else if (b.y + b.h <= a.y) {
    p0 = [a.x + a.w / 2, a.y]; p3 = [b.x + b.w / 2, b.y + b.h + 5];
    const dy = Math.max(16, (p0[1] - p3[1]) / 2);
    c1 = [p0[0], p0[1] - dy]; c2 = [p3[0], p3[1] + dy];
  } else return null;
  const at = (t: number): [number, number] => {
    const u = 1 - t;
    return [0, 1].map((k) => u * u * u * p0[k]! + 3 * u * u * t * c1[k]! + 3 * u * t * t * c2[k]! + t * t * t * p3[k]!) as [number, number];
  };
  return { d: `M${p0[0]},${p0[1]} C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p3[0]},${p3[1]}`, at };
}

type Flow = "http" | "sql" | "async" | "files" | "aws" | "dns";
export function flowOf(label: string): Flow {
  if (/SQL/.test(label)) return "sql";
  if (/^files|S3 \(OAC\)|S3 API/.test(label)) return "files";
  if (/SSM|at ship|at start|refs|metrics|bundle|API$/.test(label) && !/HTTPS API/.test(label)) return "aws";
  if (/DNS|^A |^A$|alias|CNAME|ACME|names$/.test(label)) return "dns";
  if (/gRPC|SSE|WebSocket|AMQP|Redis/.test(label)) return "async";
  return "http";
}
const FLOW: Record<Flow, { colour: string; dash?: string }> = {
  http: { colour: "var(--text)" },
  sql: { colour: "var(--accent)" },
  async: { colour: "var(--positive)" },
  files: { colour: "var(--muted)", dash: "2 3" },
  aws: { colour: "var(--warning)", dash: "7 3" },
  dns: { colour: "var(--muted)", dash: "1 3" },
};

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const overlaps = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export function Diagram({ g, label = "Architecture diagram" }: { g: ArchGraph; label?: string }) {
  const { width, height, pos, groupBoxes, within } = useMemo(() => layout(g), [g]);
  const [hover, setHover] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [fit, setFit] = useState(false);
  const focus = pinned ?? hover;

  const drawn = useMemo(() => {
    // obstacles: every node and every group's title
    const taken: Box[] = [
      ...g.nodes.map((n) => pos.get(n.id)).filter((b): b is Box => !!b),
      ...groupBoxes.map(({ g: gr, box }) => ({ x: box.x, y: box.y, w: box.w, h: headerH(gr) + 2 })),
    ];
    const out: { e: ArchEdge; c: Curve; label: { x: number; y: number; text: string } | null }[] = [];
    for (const e of g.edges) {
      const a = pos.get(e.from), b = pos.get(e.to);
      if (!a || !b || within(e.from, e.to) || within(e.to, e.from)) continue;
      const c = curve(a, b);
      if (!c) continue;
      let lab: { x: number; y: number; text: string } | null = null;
      if (e.label) {
        const text = clip(e.label, 34);
        const w = text.length * 5.6 + 8, h = 13;
        search: for (const dy of [0, -11, 11, -22, 22, -33, 33]) {
          for (const t of [0.5, 0.38, 0.62, 0.28, 0.72, 0.18, 0.82, 0.1, 0.9]) {
            const [x, y] = c.at(t);
            const box = { x: x - w / 2, y: y + dy - h + 3, w, h };
            if (taken.some((o) => overlaps(o, box))) continue;
            taken.push(box);
            lab = { x, y: y + dy, text };
            break search;
          }
        }
        // nowhere free: the label stays in the edge's tooltip only
      }
      out.push({ e, c, label: lab });
    }
    return out;
  }, [g, pos, groupBoxes, within]);

  const lit = (e: ArchEdge) => !focus || e.from === focus || e.to === focus || within(e.from, focus) || within(e.to, focus);
  const name = (id: string) => g.nodes.find((n) => n.id === id)?.label ?? g.groups?.find((x) => x.id === id)?.label ?? id;
  const sorted = [...groupBoxes].sort((a, b) => a.depth - b.depth);

  return (
    <div>
      <div className="arch-tools">
        <button className="btn ghost small" onClick={() => setFit((f) => !f)} aria-pressed={fit}>{fit ? "Actual size" : "Fit to width"}</button>
        {pinned && <button className="btn ghost small" onClick={() => setPinned(null)}>Show all links</button>}
      </div>
      <div className="arch-scroll">
        <svg className="arch" width={fit ? "100%" : width} height={fit ? undefined : height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} style={fit ? { maxWidth: width } : undefined}>
          <defs>
            {["text", "accent", "muted", "positive", "negative", "warning"].map((c) => (
              <marker key={c} id={`arrow-${c}`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" fill={`var(--${c})`} />
              </marker>
            ))}
          </defs>
          {sorted.map(({ g: gr, box, depth }) => (
            <g key={gr.id} className={`arch-group ${gr.kind}${depth % 2 ? " odd" : ""}`} onMouseEnter={() => setHover(gr.id)} onMouseLeave={() => setHover(null)} onClick={(ev) => { ev.stopPropagation(); setPinned((p) => (p === gr.id ? null : gr.id)); }}>
              <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={gr.kind === "account" || gr.kind === "internet" || gr.kind === "outside" ? 12 : 8} />
              <text x={box.x + 10} y={box.y + 16} className="arch-group-label">{clip(gr.label, Math.max(8, Math.floor((box.w - 16) / 6.6)))}</text>
              {gr.detail?.length ? <text x={box.x + 10} y={box.y + 30} className="arch-detail">{clip(gr.detail.join(" · "), Math.max(8, Math.floor((box.w - 16) / 5.8)))}</text> : null}
              <title>{[gr.label, ...(gr.detail ?? [])].join("\n")}</title>
            </g>
          ))}
          {drawn.map(({ e, c }) => {
            const f = FLOW[flowOf(e.label)];
            const colour = e.warning ? "var(--negative)" : f.colour;
            const marker = colour.slice(6, -1);
            return (
              <g key={`${e.from}>${e.to}`} opacity={lit(e) ? (e.inferred ? 0.6 : 1) : 0.1}>
                <path d={c.d} fill="none" stroke={colour} strokeWidth={e.warning ? 1.6 : 1.15} strokeDasharray={e.warning ? "5 3" : e.inferred ? "3 4" : f.dash} markerEnd={`url(#arrow-${marker})`}>
                  <title>{`${name(e.from)} → ${name(e.to)}: ${e.label || "(link)"}${e.inferred ? " (likely, not read from configuration)" : ""}${e.warning ? `\n${e.warning}` : ""}`}</title>
                </path>
              </g>
            );
          })}
          {g.nodes.map((n) => {
            const b = pos.get(n.id);
            if (!b) return null;
            const badge = n.aws ?? (n.labEdge ? "lab-edge" : "");
            const chars = Math.floor((b.w - 20 - (badge ? badge.length * 6 + 8 : 0)) / (n.compact ? 6.4 : 7));
            return (
              <g key={n.id} onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)} onClick={(ev) => { ev.stopPropagation(); setPinned((p) => (p === n.id ? null : n.id)); }} className={`arch-node ${n.kind}${n.muted ? " muted" : ""}${n.warning ? " warn" : ""}${n.compact ? " compact" : ""}${focus === n.id ? " focus" : ""}`}>
                <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={n.kind === "database" ? 12 : 6} />
                <text x={b.x + 9} y={b.y + (n.compact ? 17 : 19)} className="arch-label">{clip(n.label, chars)}</text>
                {(n.aws || n.labEdge) && <text x={b.x + b.w - 7} y={b.y + (n.compact ? 16 : 18)} textAnchor="end" className="arch-tag">{n.aws ?? "lab-edge"}</text>}
                {n.detail.slice(0, n.compact ? 1 : 2).map((d, i) => <text key={i} x={b.x + 9} y={b.y + (n.compact ? 31 : 35) + i * 14} className="arch-detail">{clip(d, Math.floor((b.w - 16) / 5.8))}</text>)}
                {n.warning && <text x={b.x + b.w - 8} y={b.y + b.h - 6} textAnchor="end" className="arch-warn">!</text>}
                <title>{[n.label, ...n.detail, n.aws ? `AWS ${n.aws}` : "", n.labEdge ? "on the shared lab-edge network" : "", n.warning ?? ""].filter(Boolean).join("\n")}</title>
              </g>
            );
          })}
          {drawn.map(({ e, label: l }) => {
            if (!l) return null;
            const colour = e.warning ? "var(--negative)" : FLOW[flowOf(e.label)].colour;
            return <text key={`l:${e.from}>${e.to}`} x={l.x} y={l.y} textAnchor="middle" className="arch-edge-label" fill={colour} opacity={lit(e) ? 1 : 0.1}>{l.text}</text>;
          })}
        </svg>
      </div>
    </div>
  );
}

export function Legend() {
  const line = (f: Flow) => <svg width="28" height="10" aria-hidden="true"><line x1="0" y1="5" x2="28" y2="5" stroke={FLOW[f].colour} strokeWidth="1.4" strokeDasharray={FLOW[f].dash} /></svg>;
  const box = (cls: string) => <svg width="22" height="14" aria-hidden="true" className={`arch-legend-box ${cls}`}><rect x="1" y="1" width="20" height="12" rx="3" /></svg>;
  return (
    <div className="arch-legend small muted">
      <span>{line("http")} HTTP(S)</span>
      <span>{line("sql")} SQL</span>
      <span>{line("async")} gRPC, SSE, queues</span>
      <span>{line("files")} files, S3</span>
      <span>{line("aws")} AWS API (SSM, Secrets Manager, S3 bundles)</span>
      <span>{line("dns")} DNS</span>
      <span><svg width="28" height="10" aria-hidden="true"><line x1="0" y1="5" x2="28" y2="5" stroke="var(--muted)" strokeWidth="1.4" strokeDasharray="3 4" /></svg> likely, not configured</span>
      <span><svg width="28" height="10" aria-hidden="true"><line x1="0" y1="5" x2="28" y2="5" stroke="var(--negative)" strokeWidth="1.6" strokeDasharray="5 3" /></svg> problem</span>
      <span>{box("account")} AWS account</span>
      <span>{box("region")} region</span>
      <span>{box("vpc")} VPC</span>
      <span>{box("host")} host</span>
      <span>{box("compose")} compose project, environment, cluster</span>
      <span><span className="arch-tag-html">RDS</span> the AWS service</span>
      <span><span className="arch-tag-html">lab-edge</span> on the network Caddy reaches</span>
      <span><span className="arch-muted-box" /> named, not set, stopped</span>
      <span>Arrows point from the caller. Tap a box to show only its links.</span>
    </div>
  );
}
